import { FC, Ref } from 'react';
import { IonInput, IonText } from '@ionic/react';
import { isPlatform } from '../utils/isPlatform';
import { ITextInputProps } from './ITextInputProps';
import { createUseStyles } from 'react-jss';

type IStyleKeys = 'input';

interface IStyleProps {
    width?: number;
    readOnly?: boolean;
    labelPlacement?: string;
};

const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    input: ({ width, readOnly, labelPlacement }) => ({
        width,

        // reduce end label margin from 16 to 2
        '& .label-text-wrapper': {
            marginLeft: labelPlacement === 'end' ? '2px!important' : undefined
        },

        // if read-only, convert from disabled styling
        color: readOnly ? 'initial' : undefined,
        opacity: readOnly ? '1 !important' : undefined,
        '--border-color': readOnly? 'transparent !important' : undefined,

        // native devices need more
        '& input': readOnly ? {
            color: 'initial',
            opacity: '1 !important',
        } : undefined
    })
});

export type IInputType = 'text' | 'number';

export type IInputMode = 'text' | 'numeric';

export type ITextFieldProps = ITextInputProps & {
    inputRef?: Ref<HTMLIonInputElement>;
    autoFocus?: boolean;
    blurOnEnter?: boolean;
    onFocus?: () => void;
    required?: boolean;
    type?: IInputType;
    inputmode?: IInputMode;
    width?: number;
};

export const TextField: FC<ITextFieldProps> = ({
    name,
    label,
    labelPlacement = 'stacked',
    value,
    placeholder,
    'data-id': dataId,
    'data-type': dataType,
    onChange = () => {},
    onInput = () => {},
    onFocus,
    inputRef,
    readOnly,
    autoFocus,
    autocapitalize,
    enterkeyhint,
    blurOnEnter = false,
    required = false,
    type,
    inputmode,
    width
}) => {
    const classes = useStyles({width, readOnly, labelPlacement});

    // don't autofocus on mobile browser
    const canAutoFocus = () => {
        return autoFocus && (isPlatform('desktop') || isPlatform('capacitor'));
    };

    const onKeyUp = (ev: React.KeyboardEvent<HTMLIonInputElement>) => {
        if (blurOnEnter && isPlatform('capacitor') && ev.code === 'Enter') {
            (ev.nativeEvent?.target as HTMLIonInputElement).blur();
        }
    };

    // only allow digits in numeric field (must be excluded here, because
    // browser strips them from the values sent to onInput, onChange)
    const onKeyDown = (ev: any) => {
        if (type === 'number' && ['e', 'E', '+', '-'].includes(ev.key)) {
            ev.preventDefault();
            return false;
        }
    };

    enterkeyhint = !enterkeyhint && blurOnEnter ? 'done' : enterkeyhint;

    return (
        <IonInput
            ref={inputRef}
            name={name}
            value={value}
            label={!required ? label : undefined}
            fill="outline"
            enterkeyhint={enterkeyhint}
            data-id={dataId}
            data-type={dataType}
            placeholder={placeholder}
            labelPlacement={label ? labelPlacement : 'end'}
            autocapitalize={autocapitalize}
            autofocus={canAutoFocus()}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            disabled={readOnly}
            type={type}
            inputmode={inputmode}
            className={classes.input}
            onIonChange={(e) => onChange(e.detail.value || '')}
            onIonInput={(e) => onInput(e.detail.value || '')}
            onIonFocus={onFocus}
        >
            {required && (
                <div slot="label">
                    <div style={{display: 'inline-block', marginBottom: 10}}>{label}&nbsp;</div>
                    <IonText style={{fontSize: 22}} color="danger">*</IonText>
                </div>
            )}
        </IonInput>
    );
};
